<script setup>
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';

import { api } from '@/api/client.js';
import AnalyticsChart from '@/components/charts/AnalyticsChart.vue';
import Button from '@/components/ds/Button.vue';
import PanelCard from '@/components/panels/PanelCard.vue';
import { usePanelState } from '@/composables/usePanelState.js';
import { usePoll } from '@/composables/usePoll.js';
import { useTenantStore } from '@/stores/tenant.js';

const tenant = useTenantStore();
const { activeAppId, timeWindow } = storeToRefs(tenant);
const ps = usePanelState('loading');
const summary = ref(null);
const charts = ref({ buckets: [], environments: [] });
const environment = ref('');

async function fetchFn({ signal }) {
  if (!activeAppId.value) return null;
  const filters = { appId: activeAppId.value, environment: environment.value || undefined, window: timeWindow.value, signal };
  const [overview, statistics] = await Promise.all([
    api.events.syntheticSummary(filters),
    api.events.syntheticCharts(filters),
  ]);
  if (overview?.ok !== true || statistics?.ok !== true) throw new Error('Invalid E2E statistics response');
  return { summary: overview.summary, charts: statistics.charts };
}

const { tick, status: refreshStatus, lastUpdatedAt } = usePoll(fetchFn, {
  watch: [() => tenant.changeKey, environment],
  onSuccess(result) {
    summary.value = result?.summary ?? null;
    charts.value = result?.charts ?? { buckets: [], environments: [] };
    ps.setState(summary.value?.total > 0 ? 'data' : 'empty');
  },
  onError(cause) {
    ps.setState('error', { message: 'Could not load E2E statistics', detail: cause?.message ?? '' });
  },
});

watch(() => tenant.changeKey, () => {
  summary.value = null;
  charts.value = { buckets: [], environments: [] };
  ps.setState('loading');
});
watch(environment, () => {
  summary.value = null;
  charts.value = { buckets: [], environments: [] };
  ps.setState('loading');
});
ps.setRetryHandler(() => tick());
defineExpose({ ps, refreshStatus });

const detailsTo = computed(() => ({
  name: 'apps-e2e',
  params: { appId: activeAppId.value },
  query: { window: timeWindow.value, ...(environment.value ? { environment: environment.value } : {}) },
}));
const outcomeLabels = ['Passed', 'Failed', 'Pending publication'];
const outcomeData = computed(() => [summary.value?.passed ?? 0, summary.value?.failed ?? 0, summary.value?.pending ?? 0]);
const outcomeDataset = computed(() => [{ label: 'Runs', data: outcomeData.value, backgroundColor: ['var(--success-icon)', 'var(--danger-icon)', 'var(--warning-icon)'] }]);
const trendLabels = computed(() => (charts.value.buckets ?? []).map(({ at }) => {
  const date = new Date(at);
  return Number.isNaN(date.getTime()) ? at : date.toLocaleString(undefined, ['1h', '24h'].includes(timeWindow.value) ? { hour: '2-digit', minute: '2-digit' } : { month: 'short', day: 'numeric' });
}));
const trendDatasets = computed(() => [
  { label: 'Passed', data: (charts.value.buckets ?? []).map((b) => b.passed), borderColor: 'var(--success-icon)' },
  { label: 'Failed', data: (charts.value.buckets ?? []).map((b) => b.failed), borderColor: 'var(--danger-icon)' },
  { label: 'Pending', data: (charts.value.buckets ?? []).map((b) => b.pending), borderColor: 'var(--warning-icon)' },
]);
const environmentLabels = computed(() => (charts.value.environments ?? []).map((e) => e.environment));
const environmentDatasets = computed(() => [
  { label: 'Passed', data: (charts.value.environments ?? []).map((e) => e.passed), backgroundColor: 'var(--success-icon)' },
  { label: 'Failed', data: (charts.value.environments ?? []).map((e) => e.failed), backgroundColor: 'var(--danger-icon)' },
  { label: 'Pending', data: (charts.value.environments ?? []).map((e) => e.pending), backgroundColor: 'var(--warning-icon)' },
]);
const outcomeAria = computed(() => `${summary.value?.passed ?? 0} passed, ${summary.value?.failed ?? 0} failed, ${summary.value?.pending ?? 0} pending E2E runs`);
const trendAria = computed(() => `E2E run outcomes over ${timeWindow.value}: ${outcomeAria.value}`);
const environmentAria = computed(() => `E2E run outcomes by environment: ${environmentLabels.value.join(', ')}`);
</script>

<template>
  <PanelCard
    title="E2E tests"
    :state="ps.state.value"
    empty-headline="No E2E results in this period"
    empty-body="Runs will appear after the runner publishes remote tests."
    :error-message="ps.errorMessage.value"
    :error-detail="ps.errorDetail.value"
    :on-retry="ps.retry"
    :refresh-status="refreshStatus"
    :last-updated-at="lastUpdatedAt"
  >
    <template #header>
      <div class="synthetic-panel__controls">
        <label class="synthetic-panel__filter">
          <span class="visually-hidden">E2E environment</span>
          <select
            v-model="environment"
            aria-label="E2E environment"
          >
            <option value="">All environments</option>
            <option value="testing">Testing</option>
            <option value="stage">Stage</option>
            <option value="production">Production</option>
          </select>
        </label>
        <RouterLink
          :to="detailsTo"
          class="synthetic-panel__detail-link"
        >
          Details
        </RouterLink>
      </div>
    </template>

    <p
      v-if="summary?.state === 'pending' || summary?.state === 'stale'"
      class="synthetic-panel__notice"
      role="status"
    >
      {{ summary.state === 'pending' ? 'The latest run is still publishing.' : 'The latest run is outside this period.' }}
    </p>
    <div class="synthetic-panel__metrics">
      <div><span>Runs</span><strong>{{ summary?.total ?? 0 }}</strong></div>
      <div><span>Case pass rate</span><strong>{{ summary?.passRatePct == null ? '—' : `${summary.passRatePct}%` }}</strong></div>
      <div><span>p95 duration</span><strong>{{ summary?.durationP95Ms == null ? '—' : `${summary.durationP95Ms} ms` }}</strong></div>
    </div>
    <div class="synthetic-panel__charts">
      <section aria-label="E2E outcomes">
        <h3>Run outcomes</h3>
        <AnalyticsChart
          type="doughnut"
          :labels="outcomeLabels"
          :datasets="outcomeDataset"
          :aria-label="outcomeAria"
          :center-value="String(summary?.total ?? 0)"
          center-label="runs"
        />
      </section>
      <section aria-label="E2E trend">
        <h3>Outcomes over time</h3>
        <AnalyticsChart
          type="bar"
          index-axis="x"
          :labels="trendLabels"
          :datasets="trendDatasets"
          :aria-label="trendAria"
        />
      </section>
      <section
        v-if="environmentLabels.length > 1"
        class="synthetic-panel__wide"
        aria-label="E2E environments"
      >
        <h3>Runs by environment</h3>
        <AnalyticsChart
          type="bar"
          :labels="environmentLabels"
          :datasets="environmentDatasets"
          :aria-label="environmentAria"
        />
      </section>
    </div>
    <div class="synthetic-panel__actions">
      <Button
        as="RouterLink"
        :to="detailsTo"
        variant="secondary"
        size="sm"
        icon-right="arrowRight"
      >
        View runs and evidence
      </Button>
    </div>
    <template #tableFallback>
      <table class="synthetic-panel__table">
        <caption class="visually-hidden">
          E2E runs by environment
        </caption>
        <thead>
          <tr>
            <th scope="col">
              Environment
            </th><th scope="col">
              Passed
            </th><th scope="col">
              Failed
            </th><th scope="col">
              Pending
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="item in charts.environments"
            :key="item.environment"
          >
            <td>{{ item.environment }}</td><td>{{ item.passed }}</td><td>{{ item.failed }}</td><td>{{ item.pending }}</td>
          </tr>
        </tbody>
      </table>
    </template>
  </PanelCard>
</template>

<style scoped>
.synthetic-panel__controls {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: var(--space-3);
}

.synthetic-panel__detail-link {
  color: var(--accent-fg-on-soft);
  font-size: var(--text-sm);
  text-decoration: none;
}

.synthetic-panel__detail-link:hover {
  text-decoration: underline;
}

.synthetic-panel__detail-link:focus-visible {
  outline: var(--bw-thick) solid var(--accent-border);
  outline-offset: var(--space-1);
}

.synthetic-panel__filter select {
  min-height: var(--control-md);
  padding: 0 var(--space-3);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-md);
  background: var(--bg-canvas);
  color: var(--fg-primary);
  font: inherit;
  font-size: var(--text-sm);
}

.synthetic-panel__metrics {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-5);
  margin-bottom: var(--space-5);
}

.synthetic-panel__metrics div {
  display: grid;
  gap: var(--space-1);
}

.synthetic-panel__metrics span {
  color: var(--fg-secondary);
  font-size: var(--text-xs);
}

.synthetic-panel__metrics strong {
  font-size: var(--text-xl);
  font-variant-numeric: tabular-nums;
}

.synthetic-panel__charts {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: var(--space-5);
}

.synthetic-panel__charts section {
  min-width: 0;
  padding: var(--space-4);
  border: var(--bw-thin) solid var(--border-default);
  border-radius: var(--radius-xl);
}

.synthetic-panel__charts h3 {
  margin: 0 0 var(--space-3);
  color: var(--fg-secondary);
  font-size: var(--text-sm);
}

.synthetic-panel__wide {
  grid-column: 1 / -1;
}

.synthetic-panel__notice {
  margin: 0 0 var(--space-4);
  color: var(--warning-fg);
  font-size: var(--text-sm);
}

.synthetic-panel__actions {
  display: flex;
  justify-content: flex-end;
  margin-top: var(--space-4);
}

.synthetic-panel__table {
  width: 100%;
  border-collapse: collapse;
}

.synthetic-panel__table th,
.synthetic-panel__table td {
  padding: var(--space-2);
  border-bottom: var(--bw-thin) solid var(--border-default);
  text-align: left;
}

@media (width <= 767px) {
  .synthetic-panel__charts {
    grid-template-columns: 1fr;
  }
}
</style>
